/**
 * Where the app looks for the download-on-demand chart packs.
 *
 * One base URL, set at build time, with the pack sets hanging off it. It is an
 * env var rather than a constant in here because the same source has to build
 * against production, a staging bucket and a local server, and editing a source
 * file to switch between them is how the wrong one ends up shipped.
 *
 *   VITE_PACKS_BASE=https://charts.example.com npm run build
 *
 * Put it in .env.local for day-to-day work. If it is not set, the old GitHub path
 * is used, which still serves the Antares raster packs. That default is on its
 * way out: the NOAA vector set is 9.3 GB, which is far past what a git repo
 * should hold, so those live on R2. See docs/pack-hosting.md.
 *
 * A base the app cannot reach is not an error. The More-charts store shows the
 * pack sets it could fetch and stays quiet about the rest.
 */
const DEFAULT_PACKS_BASE = 'https://raw.githubusercontent.com/ArdentTraining/ardent-chart-packs/main';

const PACKS_BASE = (
  (import.meta.env as Record<string, string | undefined>).VITE_PACKS_BASE ?? DEFAULT_PACKS_BASE
).replace(/\/+$/, '');

/** The Antares raster packs, built by `npm run build-chart-packs`. */
export const PACKS_MANIFEST_URL = `${PACKS_BASE}/charts-v1/packs.json`;

/**
 * The VECTOR packs, built by `npm run build-enc-packs`. They have their own
 * manifest rather than joining the raster list because they come from a different
 * pipeline and, for NOAA at least, are public-domain data that can be published
 * openly. Licensed chart data must not go on an open URL, see CHART_PACKS.md.
 */
export const ENC_PACKS_MANIFEST_URL = `${PACKS_BASE}/enc-v1/packs.json`;

/**
 * The PRIVATE pack host: the charts-gate Worker in front of the private R2
 * bucket, where the LICENSED packs (Antares) live. Unset in a build that has
 * no private charts. Every fetch against this base carries the access token
 * from settings (see chartAccess.ts); without a token the gate answers 401
 * and the pack list simply doesn't show these packs.
 */
const PRIVATE_PACKS_BASE = (
  (import.meta.env as Record<string, string | undefined>).VITE_PRIVATE_PACKS_BASE ?? ''
).replace(/\/+$/, '');

export const PRIVATE_PACKS_MANIFEST_URL = PRIVATE_PACKS_BASE
  ? `${PRIVATE_PACKS_BASE}/charts-v1/packs.json`
  : null;

/** True when this URL points at the private (token-gated) pack host. */
export function isPrivatePackUrl(url: string): boolean {
  return PRIVATE_PACKS_BASE !== '' && url.startsWith(PRIVATE_PACKS_BASE);
}

/** True when a private pack host is configured in this build at all. */
export function hasPrivatePackHost(): boolean {
  return PRIVATE_PACKS_BASE !== '';
}

/** Every pack list the app checks. A list that cannot be reached is skipped. */
export const PACKS_MANIFEST_URLS = [
  PACKS_MANIFEST_URL,
  ENC_PACKS_MANIFEST_URL,
  ...(PRIVATE_PACKS_MANIFEST_URL ? [PRIVATE_PACKS_MANIFEST_URL] : []),
];
